import { useCallback, useEffect, useState } from "react";

import { emptyArrayFor1Attach, resolveAttachmentUrls } from "@common/components/DetailsPane/helper";
import displayToast from "@common/components/Toast/displayToast";
import useAbortController from "@common/hooks/useAbortController";
import { App, Attachment, PlatformName, SupportedFileTypes } from "@common/models";
import { mapToMessage } from "@common/models/issueTracking";
import { resolveFileType } from "@common/util/files";
import { setAppAction, setLoadingAction } from "@public/context/App/App.actions";
import { useAppDispatch } from "@public/context/App/App.context";
import { RoutePaths } from "@public/router/config/routePaths";
import { useQuery } from "@tanstack/react-query";
import { saveAs } from "file-saver";
import { generatePath, useNavigate } from "react-router-dom";

import { useUserFeedbackDeps } from "../UserFeedback";

interface Params {
  issueId?: string;
}

export default function useUserFeedbackDetailsApiConsumer({ issueId }: Params) {
  const [error, setError] = useState<unknown>();
  const { userFeedbackService } = useUserFeedbackDeps();
  const appDispatch = useAppDispatch();
  const { abortSignal } = useAbortController();
  const [mappedAttachments, setMappedAttachments] = useState<Attachment[]>([]);
  const navigate = useNavigate();

  const getUserFeedbackBlackbox = async () => {
    if (!issue?.id) return;

    try {
      const { data } = await userFeedbackService.getBlackbox(issue.id);

      return data;
    } catch (error) {
      displayToast({
        title: "Something went wrong",
        content: "Please try again.",
      });
    }
  };

  const getUserFeedbackDetails = async () => {
    if (!issueId) return undefined;

    setError(undefined);
    appDispatch(setLoadingAction(true));
    try {
      return await userFeedbackService
        .getUserFeedback(issueId, abortSignal)
        .then(({ data }) => {
          appDispatch(setLoadingAction(false));
          appDispatch(setAppAction(data.app as App));
          return data;
        })
        .catch(() => {
          navigate(generatePath(RoutePaths.NOT_FOUND));
          return undefined;
        });
    } catch (error) {
      if (abortSignal.aborted) return undefined;
      setError(error);
      appDispatch(setLoadingAction(false));
      return undefined;
    }
  };

  const getUserFeedbackChat = async () => {
    if (!issue?.id) return;

    try {
      const { data } = await userFeedbackService.getChat(issue.id);
      return data.map(mapToMessage);
    } catch (error) {
      displayToast({
        title: "Something went wrong",
        content: "Please try again.",
      });
    }
  };

  const { data: issue } = useQuery([`ticket`, { issueId }], getUserFeedbackDetails, {
    refetchOnWindowFocus: false,
    cacheTime: Infinity,
  });

  const { data: blackboxData } = useQuery([`blackbox`, { issueId }], getUserFeedbackBlackbox, {
    enabled: issue && issue.id ? true : false,
    refetchOnWindowFocus: false,
    cacheTime: Infinity,
  });

  const { data: chatData } = useQuery([`chat`, { issueId }], getUserFeedbackChat, {
    enabled: issue && issue.id ? true : false,
    refetchOnWindowFocus: false,
    cacheTime: Infinity,
  });

  const downloadActivityHistory = async (ticketId?: string) => {
    if (!ticketId) return;

    try {
      const { data } = await userFeedbackService.downloadActivityHistory(ticketId);

      const blob = new Blob([data], { type: "text/plain;charset=utf-8" });
      saveAs(blob, `${issue?.pretty_title}.log`);

      displayToast({
        title: "Fantastic!",
        content: `Activity history has been successfully downloaded`,
      });
    } catch (error) {
      displayToast({ title: "Something went wrong", content: "Please try again." });
    }
    return;
  };

  const mapAttachment = useCallback(async (url: string) => {
    if (resolveFileType(url) === SupportedFileTypes.TXT) {
      await fetch(url, { method: "GET", mode: "cors" })
        .then((response) => response.text())
        .then((t) => {
          setMappedAttachments((mappedAttachments) => {
            return [...mappedAttachments, { url: url, text: t, num: 3 }];
          });
        })
        .catch(() => console.error("err"));
    } else {
      setMappedAttachments((mappedAttachments) => {
        return [...mappedAttachments, { url: url, num: getAttachmentOrderNum(resolveFileType(url)) }];
      });
    }
  }, []);

  useEffect(() => {
    if (issue && !mappedAttachments.length) {
      resolveAttachmentUrls(issue)?.map((url) => {
        mapAttachment(url);
      });

      if (resolveAttachmentUrls(issue).length === 1 && issue.files.length === 0) {
        setMappedAttachments((mappedAttachments) => {
          return [...mappedAttachments, ...emptyArrayFor1Attach("large", PlatformName.WEB)];
        });
      }

      if (
        !Boolean(resolveAttachmentUrls(issue).length) ||
        (resolveAttachmentUrls(issue).length === 2 && !issue.files.length)
      ) {
        setMappedAttachments((mappedAttachments) => {
          return [...mappedAttachments, ...emptyArrayFor1Attach("small", PlatformName.WEB)];
        });
      }
    }
  }, [issue, mapAttachment, mappedAttachments]);

  return {
    userFeedbackDetails: issue,
    downloadActivityHistory,
    error,
    mappedAttachments,
    blackbox: blackboxData,
    messages: chatData,
  };
}

const getAttachmentOrderNum = (type?: SupportedFileTypes) => {
  switch (type) {
    case SupportedFileTypes.IMAGE:
    case SupportedFileTypes.VIDEO:
      return 1;
    case SupportedFileTypes.TXT:
      return 2;
    default:
      return 3;
  }
};
